import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiConfig, StoreApiConfig } from '../config/apiConfig';
import { GeneralConfig } from '../config/generalConfig';
import {
  buildUrl,
  parseApiErrorResponse,
  parseApiSuccessResponse,
} from '../utils/apiUtils';
import { getAuthToken, getDemoPlan, getStoreAuthToken } from '../utils/storage';
import { NetworkException } from '../utils/apiException';
import { isModuleLoaded } from '../stores/moduleStore';

export class StoreApiService {
  get host(): string {
    return StoreApiConfig.host;
  }

  get protocol(): string {
    return StoreApiConfig.protocol;
  }

  get pathPrefix(): string {
    return StoreApiConfig.pathPrefix;
  }

  get headers(): Record<string, string> {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'App-Key': StoreApiConfig.appKey,
    };
  }

  get authHeader(): Record<string, string> {
    const storeAuthToken = getStoreAuthToken();
    if (storeAuthToken) {
      return {
        'Auth-Token': storeAuthToken,
      };
    }
    return {};
  }

  async get(
    path: string,
    options: {
      queryParams?: Record<string, any>;
      authenticate?: boolean;
      prefix?: string;
      includeImappToken?: boolean;
    } = {}
  ): Promise<any> {
    const {
      queryParams = {},
      authenticate = true,
      prefix,
      includeImappToken = false,
    } = options;

    let fullPath = `${this.pathPrefix}${path}`;
    if (prefix) {
      fullPath = `${prefix}${path}`;
    }

    queryParams['route'] = `app${path}`;

    if (GeneralConfig.isDemo) {
      const demoPlan = getDemoPlan();
      if (demoPlan) {
        queryParams['demo_plan'] = demoPlan;
      }
    }

    if (!isModuleLoaded()) {
      queryParams['load_modules'] = '1';
    }

    const url = buildUrl(this.protocol, this.host, fullPath, queryParams);

    const config: AxiosRequestConfig = {
      url,
      method: 'GET',
      headers: {
        ...this.headers,
        ...(authenticate || !isModuleLoaded() ? this.authHeader : {}),
        ...(includeImappToken ? { 'Imapp-Token': getAuthToken() || '' } : {}),
      },
    };

    try {
      const response: AxiosResponse = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async postJson(
    path: string,
    options: {
      queryParams?: Record<string, any>;
      data?: Record<string, any>;
      authenticate?: boolean;
      prefix?: string;
      includeImappToken?: boolean;
    } = {}
  ): Promise<any> {
    const {
      queryParams = {},
      data,
      authenticate = true,
      prefix,
      includeImappToken = false,
    } = options;

    let fullPath = `${this.pathPrefix}${path}`;
    if (prefix) {
      fullPath = `${prefix}${path}`;
    }

    queryParams['route'] = `app/${path}`;

    if (GeneralConfig.isDemo) {
      const demoPlan = getDemoPlan();
      if (demoPlan) {
        queryParams['demo_plan'] = demoPlan;
      }
    }

    const url = buildUrl(this.protocol, this.host, fullPath, queryParams);

    const config: AxiosRequestConfig = {
      url,
      method: 'POST',
      headers: {
        ...this.headers,
        ...(authenticate ? this.authHeader : {}),
        ...(includeImappToken ? { 'Imapp-Token': getAuthToken() || '' } : {}),
      },
      data: data ? JSON.stringify(data) : undefined,
    };

    try {
      const response: AxiosResponse = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }
}
